import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const DROPDOWN_MAX_HEIGHT = 240;

export default function CityListAutocomplete({
  cities = [],
  value,
  onChange,
  onSelect,
  placeholder = "Search city…",
  className,
  inputClassName,
  inputStyle,
  containerTestId,
  inputTestId,
  excludeCities = [],
  clearInputOnSelect = false,
  suggestionsPlacement = "auto",
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const excludedCityKeys = useMemo(
    () => new Set(excludeCities.map((city) => String(city || "").trim().toLowerCase()).filter(Boolean)),
    [excludeCities]
  );

  const normalizedCities = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const city of cities) {
      const normalized = String(city || "").trim();
      if (!normalized) continue;
      const key = normalized.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(normalized);
    }
    return list.sort((a, b) => a.localeCompare(b));
  }, [cities]);

  const suggestions = useMemo(() => {
    const q = String(value || "").trim().toLowerCase();
    if (!q) return [];
    return normalizedCities.filter(
      (city) => city.toLowerCase().includes(q) && !excludedCityKeys.has(city.toLowerCase())
    );
  }, [value, normalizedCities, excludedCityKeys]);

  const updatePlacement = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    if (suggestionsPlacement === "top") {
      setDropUp(true);
      return;
    }
    if (suggestionsPlacement === "bottom") {
      setDropUp(false);
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setDropUp(spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow);
  }, [suggestionsPlacement]);

  useEffect(() => {
    if (!open) return undefined;

    updatePlacement();
    const onReposition = () => updatePlacement();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, suggestions.length, updatePlacement]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pickCity = (city) => {
    onSelect?.(city);
    onChange(clearInputOnSelect ? "" : city);
    setOpen(false);
    setHighlightIndex(-1);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)} data-testid={containerTestId}>
      <Input
        data-testid={inputTestId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setHighlightIndex(-1);
          updatePlacement();
        }}
        onFocus={() => {
          setOpen(true);
          updatePlacement();
        }}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            setOpen(true);
            return;
          }
          if (event.key === "Escape") {
            setOpen(false);
            setHighlightIndex(-1);
            return;
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightIndex((index) => Math.min(index + 1, suggestions.length - 1));
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightIndex((index) => Math.max(index - 1, 0));
          }
          if (event.key === "Enter" && highlightIndex >= 0 && suggestions[highlightIndex]) {
            event.preventDefault();
            pickCity(suggestions[highlightIndex]);
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className={cn("h-10 rounded-none text-sm", inputClassName)}
        style={inputStyle}
      />

      {open && suggestions.length > 0 ? (
        <ul
          role="listbox"
          className={cn(
            "absolute left-0 right-0 z-[9999] overflow-y-auto rounded-none border bg-white py-1 shadow-lg",
            dropUp ? "bottom-full mb-1" : "top-full mt-1"
          )}
          style={{ maxHeight: DROPDOWN_MAX_HEIGHT, borderColor: "#E8E3D9" }}
        >
          {suggestions.map((city, index) => (
            <li key={city} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={highlightIndex === index}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm transition-colors",
                  highlightIndex === index
                    ? "bg-orange-50 text-orange-900"
                    : "text-neutral-800 hover:bg-neutral-50"
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pickCity(city)}
              >
                {city}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {open && String(value || "").trim() && suggestions.length === 0 ? (
        <div
          className={cn(
            "absolute left-0 right-0 z-[9999] rounded-none border bg-white px-3 py-2 text-xs text-neutral-500 shadow-lg",
            dropUp ? "bottom-full mb-1" : "top-full mt-1"
          )}
          style={{ borderColor: "#E8E3D9" }}
        >
          No matching cities
        </div>
      ) : null}
    </div>
  );
}
